import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { AccessIcon, LockIcon, ArrowRight01Icon, ViewIcon, ViewOffSlashIcon } from '@hugeicons/core-free-icons';
import { api } from '../utils/api';
import { FLAGS } from '../config/flags';

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      setError('Current password is required');
      return;
    }
    if (!newPassword) {
      setError('The password field is required.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    if (FLAGS.USE_MOCK_API) {
      await new Promise(resolve => setTimeout(resolve, 800));
      setSuccess(true);
      setTimeout(() => navigate('/profile'), 2000);
      setLoading(false);
      return;
    }

    try {
      await api.post('/change-password', { 
        current_password: currentPassword, 
        password: newPassword, 
        password_confirmation: confirmNewPassword 
      });
      setSuccess(true);
      setTimeout(() => navigate('/profile'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full space-y-8 bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float-lg">
        <div className="text-center">
          <h2 className="text-3xl font-black text-charcoal dark:text-white tracking-tight">Change Password</h2>
          <p className="mt-2 text-muted-gray">Update your account password</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-1">
                Current Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray">
                  <HugeiconsIcon icon={AccessIcon} size={20} />
                </div>
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="block w-full pl-10 pr-10 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-gray hover:text-charcoal dark:hover:text-white"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  <HugeiconsIcon icon={showCurrentPassword ? ViewOffSlashIcon : ViewIcon} size={20} />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-1">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray">
                  <HugeiconsIcon icon={LockIcon} size={20} />
                </div>
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={8}
                  className="block w-full pl-10 pr-10 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-gray hover:text-charcoal dark:hover:text-white"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  <HugeiconsIcon icon={showNewPassword ? ViewOffSlashIcon : ViewIcon} size={20} />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-1">
                Confirm New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray">
                  <HugeiconsIcon icon={LockIcon} size={20} />
                </div>
                <input
                  type={showConfirmNewPassword ? "text" : "password"}
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  minLength={8}
                  className="block w-full pl-10 pr-10 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-gray hover:text-charcoal dark:hover:text-white"
                  onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                >
                  <HugeiconsIcon icon={showConfirmNewPassword ? ViewOffSlashIcon : ViewIcon} size={20} />
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 dark:bg-red-900/20 border border-red-200/50 text-red-700 dark:text-red-300 px-4 py-3 rounded-2xl text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-100 dark:bg-green-900/20 border border-green-200/50 text-green-700 dark:text-green-300 px-4 py-3 rounded-2xl text-sm">
              Password changed successfully!
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`group relative w-full flex justify-center py-4 px-4 border border-transparent text-sm font-bold rounded-3xl text-white bg-soft-green hover:bg-light-mint focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-soft-green transition-all duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-float disabled:opacity-50`}
          >
            {loading ? 'Updating...' : 'Change Password'}
            <span className="absolute right-4 flex items-center">
              <HugeiconsIcon icon={ArrowRight01Icon} size={18} />
            </span>
          </button>
        </form>

        <div className="text-center">
          <p className="text-sm text-muted-gray">
            Return to{' '}
            <span 
              className="font-bold text-soft-green hover:text-light-mint cursor-pointer"
              onClick={() => navigate('/profile')}
            >
              Profile
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;